import { type Query, type QueryClient, useQuery } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { guardArray } from "@msuite/utils/array";
import { client } from "@/hono";

/**
 * The stretches crews are scheduled for across a window, whichever ticket they sit on.
 *
 * Bounded by the window rather than read per ticket: the capacity board draws many ticket rows at
 * once, and one request for the visible weeks is cheaper than one per row — and cheaper than
 * carrying every stretch a crew has ever had on the crew list.
 */
export function useScheduledCrews(startDate: string, endDate: string) {
	/** Query */
	const query = useQuery({
		queryKey: ["crew-definitions", "ticket-assignments", startDate, endDate],
		queryFn: async () => {
			const response = await client.crew_definitions["ticket-assignments"].$get({
				query: { startDate, endDate },
			});
			const data = await responseHandler(response);
			return data.ticketAssignments;
		},
	});

	/** Return */
	return { ...query, ticketAssignments: guardArray(query.data) };
}

export type ScheduledCrewList = ReturnType<typeof useScheduledCrews>["ticketAssignments"];
export type ScheduledCrewListItem = ScheduledCrewList[number];

/**
 * Whether a cached query is one of this hook's, whatever window it was asked for.
 *
 * The key is nested under `crew-definitions` so an invalidation of the crews takes the stretches
 * with it; asking for both segments is what lets a write that changed only the stretches leave the
 * crew list and the absence days alone.
 */
export function isScheduledCrewsQuery({ queryKey }: Query) {
	return queryKey.includes("crew-definitions") && queryKey.includes("ticket-assignments");
}

/**
 * Put a stretch the server just stored into every window that holds it, instead of fetching the
 * stretches again.
 *
 * A crew dropped on a bar answers with the row it created — or the one already there, since a drop
 * is easy to repeat — and that row is all the board needs to draw the crew's tag. The query is left
 * stale so the next focus refreshes it.
 *
 * @param qc - The query client.
 * @param assignment - The stretch the server answered with.
 */
export async function addScheduledCrew(qc: QueryClient, assignment: ScheduledCrewListItem) {
	qc.setQueriesData<ScheduledCrewList>({ predicate: isScheduledCrewsQuery }, (assignments) => {
		if (!assignments) return assignments;
		if (assignments.some((entry) => entry.id === assignment.id)) return assignments;
		return [...assignments, assignment];
	});
	await qc.invalidateQueries({ predicate: isScheduledCrewsQuery, refetchType: "none" });
}
